/**
 * StorageManager (multi-session)
 * - Keeps backward-compatible API: constructor({ key, maxAge, onExpire }), debounceSave(), save(), load(), clear()
 * - Adds sessions per tab via sessionStorage; keys are `${key}:${sessionId}` in localStorage and IndexedDB.
 * - New methods: getSessionId(), getSessions(), getLastSession(), getSessionData(id), copyFrom(id), cleanupExpired(opts), clearAll().
 */
class StorageManager {
  DBName = 'storage-manager-db';
  constructor(settings = {}) {
    if (!settings.key) throw new Error('StorageManager: settings.key is required');
    this.settings = settings;
    this.baseKey = String(settings.key);
    this.maxAge = settings.maxAge || null;
    this.onExpire = settings.onExpire || null;
    this.autoCleanup = settings.autoCleanup !== false;
    this.useSessionFallback = false;
  
    // create a temporary identifier (until initialization completes)
    this.sessionId = null;
    this.key = this.baseKey;
  
    // start initialization
    this._initPromise = this._initSessionId();
  
    if (this.autoCleanup) {
      this.cleanupExpired().catch(() => {});
    }
  }

  async _initSessionId() {
    const ssKey = `${this.baseKey}:tabId`;
    let sid = window.name;

    // if window.name is empty — it's a new tab
    if (!sid) {
      sid = crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.name = sid;
    }

    // if sessionStorage does not yet contain id — restore it
    if (!sessionStorage.getItem(ssKey)) {
      try {
        sessionStorage.setItem(ssKey, sid);
      } catch {}
    }

    this.sessionId = sid;
    this.key = `${this.baseKey}:${sid}`;
  }

  /** PUBLIC: returns current session id (new) */
  getSessionId() {
    return this.sessionId;
  }

  /** Convert Blob/File to Base64 string (used in fallback) */
  _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  /** Write blob (handles fallback to localStorage) */
  async _putImage(key, blob) {
    if (!blob) return;

    try {
      const db = await this._openDB();

      await new Promise((resolve, reject) => {
        try {
          const tx = db.transaction('images', 'readwrite');
          const store = tx.objectStore('images');
          const req = store.put(blob, key);

          tx.oncomplete = () => resolve();
          tx.onabort = tx.onerror = () =>
            reject(tx.error || new Error("IndexedDB transaction failed"));
        } catch (err) {
          reject(err);
        }
      });

      // Check that IndexedDB actually saved the data (Safari sometimes "skips" the write)
      const test = await this._getImage(key);
      if (!test || !(test instanceof Blob || test instanceof File)) {
        throw new Error("IndexedDB write verification failed");
      }

    } catch (err) {
      // console.warn("IDB put failed, switching to localStorage:", err);
      this.useSessionFallback = true;

      const base64 = await this._blobToBase64(blob);
      const meta = {
        data: base64,
        mime: blob.type || "application/octet-stream",
        name: blob.name || `${key.split(":").pop() || "image"}`
      };
      localStorage.setItem(`${key}:image`, JSON.stringify(meta));
    }
  }


  /** Read blob (handles fallback to localStorage) */
  async _getImage(key) {
    try {
      const db = await this._openDB();

      const result = await new Promise((resolve, reject) => {
        try {
          const tx = db.transaction('images', 'readonly');
          const store = tx.objectStore('images');
          const req = store.get(key);

          tx.oncomplete = () => resolve(req.result || null);
          tx.onerror = tx.onabort = () =>
            reject(tx.error || new Error("IndexedDB read failed"));
        } catch (err) {
          reject(err);
        }
      });

      // Safari sometimes returns null without errors → check explicitly
      if (result && (result instanceof Blob || result instanceof File)) {
        return result;
      }
      throw new Error("IndexedDB returned invalid or empty result");
    } catch (err) {
      // console.warn("IDB read failed, using fallback:", err);
      const raw = localStorage.getItem(`${key}:image`);
      if (!raw) return null;

      try {
        const meta = JSON.parse(raw);
        const res = await fetch(meta.data);
        const blob = await res.blob();
        return new File([blob], meta.name || "restored", { type: meta.mime || blob.type });
      } catch {
        return null;
      }
    }
  }

  /** Delete blob (handles fallback to localStorage) */
  async _deleteImage(key) {
    try {
      const db = await this._openDB();
      await new Promise((resolve, reject) => {
        const tx = db.transaction('images', 'readwrite');
        const store = tx.objectStore('images');
        store.delete(key);
  
        tx.oncomplete = () => resolve();
        tx.onerror = tx.onabort = () => reject(tx.error || new Error("IndexedDB delete failed"));
      });
    } catch (err) {
      // console.warn("IDB delete failed, removing fallback:", err);
      localStorage.removeItem(`${key}:image`);
    }
  }  

  /** PRIVATE: open (and prepare) IDB stores */
  async _openDB() {
    if (this._dbPromise) return this._dbPromise; // already open — return the same promise
    this._dbPromise = new Promise((res, rej) => {
      const req = indexedDB.open(this.DBName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('images')) {
          db.createObjectStore('images');
        }
      };
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    return this._dbPromise;
  }  

  /** PRIVATE: list all session keys in LS with baseKey prefix */
  _allSessionKeys() {
    const prefix = `${this.baseKey}:`;
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix) && !k.endsWith(":image")) keys.push(k);
    }
    return keys;
  }

  /** PRIVATE: read LS record for a session key (returns {savedAt, state} or null) */
  _readLS(sessionKey) {
    try {
      const raw = localStorage.getItem(sessionKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      return data;
    } catch {
      return null;
    }
  }

  /** List non-expired session ids sorted by updated time desc (new) */
  getSessions() {
    const keys = this._allSessionKeys();
    const now = Date.now();
    const items = [];
    for (const k of keys) {
      const data = this._readLS(k);
      if (!data || !data.savedAt) continue;
      const expired = this.maxAge && (now - data.savedAt > this.maxAge);
      if (!expired) {
        const id = k.slice(this.baseKey.length + 1); // strip "baseKey:"
        items.push({ id, savedAt: data.savedAt });
      }
    }
    items.sort((a, b) => b.savedAt - a.savedAt);
    return items.map(x => x.id);
  }

  /** Return id of the most recently updated non-expired session (new) */
  getLastSession() {
    const keys = this._allSessionKeys();
    const now = Date.now();
    let best = null;
    for (const k of keys) {
      const data = this._readLS(k);
      if (!data || !data.savedAt) continue;
      const expired = this.maxAge && (now - data.savedAt > this.maxAge);
      if (expired) continue;
      if (!best || data.savedAt > best.savedAt) {
        best = { key: k, savedAt: data.savedAt };
      }
    }
    if (!best) return null;
    return best.key.slice(this.baseKey.length + 1);
  }

  /** Debounced save (keeps previous behavior) */
  debounceSave(state, imageBlob = null, delay = 500) {
    this._pendingState = state;
    this._pendingImage = imageBlob;
    clearTimeout(this._saveTimeout);
    this._saveTimeout = setTimeout(() => {
      this.save(this._pendingState, this._pendingImage);
    }, delay);
  }

  /** Clear only current session (CHANGED: previously deleted whole DB) */
  async clear() {
    // Remove LS entry for current session
    localStorage.removeItem(this.key);
    await this._deleteImage(this.key);
  }

  /** Save current session state & optional image blob (keeps behavior) */
  async save(state, imageBlob = null) {
    const data = {
      savedAt: Date.now(),
      state,
      imageKey: this.key // explicit image key reference for clarity
    };
    localStorage.setItem(this.key, JSON.stringify(data));

    if (imageBlob) {
      await this._putImage(this.key, imageBlob);
    }
  }

  /** Load current session (keeps behavior: returns { ...state, image }) */
  async load() {
    const tryRead = (key) => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };
  
    let data = tryRead(this.key);
  
    // If no data — try to recover from the last session
    if (!data) {
      const prefix = `${this.baseKey}:`;
      const sessions = Object.keys(localStorage)
        .filter(k => k.startsWith(prefix) && !k.endsWith(":image"))
        .map(k => ({ k, data: tryRead(k) }))
        .filter(o => o.data?.savedAt)
        .sort((a, b) => b.data.savedAt - a.data.savedAt);
  
      if (sessions.length) {
        const newest = sessions[0];
        const srcKey = newest.k;
        const srcData = newest.data;

        const blob = await this._getImage(srcKey);
  
        // Copy state under the new id of the current tab
        await this.save(srcData.state, blob || null);
        data = srcData;
      }
    }
  
    if (!data) return null;
  
    const expired = this.maxAge && (Date.now() - data.savedAt > this.maxAge);
    if (expired) {
      await this.clear();
      if (this.onExpire) this.onExpire();
      return null;
    }

    const blob = await this._getImage(this.key);
  
    return { ...(data.state || {}), image: blob || null };
  }    

  /** Get data of another session id (new): returns { ...state, image } or null */
  async getSessionData(sessionId) {
    const k = `${this.baseKey}:${sessionId}`;
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
    const expired = this.maxAge && (Date.now() - data.savedAt > this.maxAge);
    if (expired) return null;

    const blob = await this._getImage(k);
    return { ...(data.state || {}), image: blob || null };
  }

  /** Copy state+image from another session into the current session (new) */
  async copyFrom(sessionId) {
    const other = await this.getSessionData(sessionId);
    if (!other) return false;
    const { image, ...state } = other;
    await this.save(state, image || null);
    return true;
  }

  /**
   * Cleanup expired sessions (new)
   * - By default uses Date.now(); pass { now, dryRun } to customize.
   * - Removes LS entries and their corresponding image blobs in IDB.
   */
  async cleanupExpired(opts = {}) {
    const now = typeof opts.now === 'number' ? opts.now : Date.now();
    const dryRun = !!opts.dryRun;
    const keys = this._allSessionKeys();
    const toDelete = [];
    for (const k of keys) {
      const data = this._readLS(k);
      if (!data || !data.savedAt) continue;
      const expired = this.maxAge && (now - data.savedAt > this.maxAge);
      if (expired) toDelete.push(k);
    }
    if (dryRun || toDelete.length === 0) return toDelete;
    
    await Promise.all(toDelete.map(async (k) => {
      localStorage.removeItem(k);
      await this._deleteImage(k);
    }));
    return toDelete;
  }

  /**
   * Clear ALL sessions for this baseKey (new helper)
   * NOTE: original clear() wiped the whole DB; with multi-session this method
   * removes only records for this application (matching prefix) and their images.
   */
  async clearAll() {
    const keys = this._allSessionKeys();
    
    await Promise.all(keys.map(async (k) => {
      localStorage.removeItem(k);
      await this._deleteImage(k);
    }));
  }
}

export default StorageManager;
